import { useEventListener } from "@vueuse/core";
import type { Ref } from "vue";
import { nextTick, ref, shallowRef, watch } from "vue";

/**
 * Keyboard behaviour for a field that opens a list: `SCASelectBase`, `SCAInputTags` and
 * `SCAInputTagsInline` all run on this.
 *
 * It exists because the three had drifted: the select carried this logic inline, the tag inputs had no
 * arrow navigation at all, and "how does Tab leave an open list" was answered differently in each. The
 * list, the rows and what a selection *means* stay with the component — this owns only the highlight
 * and the keys that move it.
 *
 * What a caller gets, identically everywhere:
 *
 * - `ArrowDown` opens a closed list, then walks down; `ArrowUp` walks up. Both wrap round the ends,
 *   so one arrow key cycles the whole list.
 * - `Enter` opens a closed list, or commits the highlighted row.
 * - Opening lands the highlight on {@link UseDropdownKeyboardOptions.initialIndex} (the first row by
 *   default), so `Enter` works straight away rather than after a wasted arrow press.
 * - Typing returns the highlight to that row — after three letters `Enter` should take the best match.
 * - `Escape` closes and keeps the keystroke. `Tab` closes and lets it through, so one press both
 *   dismisses the list and moves focus on.
 * - Focus never ends up on `<body>`: see {@link UseDropdownKeyboardOptions.focusAnchor}.
 *
 * Focus opens the list, which is what makes a field usable straight off a `Tab` — bind `handleFocus`
 * and `handlePointerDown` together, or a mouse click will open and instantly dismiss it.
 */
export interface UseDropdownKeyboardOptions<T> {
  /** The list's open state, owned by the component. */
  isOpen: Ref<boolean>;
  /** The navigable rows — the same array, in the same order, the component renders into `listEl`. */
  items: Readonly<Ref<readonly T[]>>;
  /** The scrolling list element. Its element children must be the rendered rows, one per item. */
  listEl: Readonly<Ref<HTMLElement | null>>;
  /** Opens the list — what ArrowDown and Enter do on a closed field. */
  open: () => void;
  /** Commits one row. What that means is the component's business. */
  commit: (item: T) => void;
  /** While this returns true no key is handled at all — a disabled or read-only field. */
  isInert: () => boolean;
  /** The term the list is filtered by, if it has one. The highlight returns to the top when it changes. */
  searchTerm?: Readonly<Ref<string>>;
  /**
   * Row to highlight when the list opens. A single select points this at its selected option so the
   * list opens on the current value; a tag input has no such row — what it lists is what is *not*
   * picked yet — and leaves it out. Out-of-range or negative falls back to the first row.
   */
  initialIndex?: () => number;
  /**
   * The field's own tab stop, where focus belongs whenever it would otherwise be lost.
   *
   * Two cases need it, both of which used to drop focus to `<body>` and lose the user's place in the
   * form: `SCAInputTags` puts focus in the search input *inside* its popover, which is unmounted with
   * it; and a single-select `SCAInputTagsInline` hides its inline input once it is full, so committing
   * removes the focused element from the layout.
   */
  focusAnchor?: () => HTMLElement | null;
  /**
   * Takes the list out of the tab order *synchronously* — `SCADropdown`'s `hideNow`.
   *
   * Without it, `Tab` cannot both close the list and move focus on in one press: the popover is still
   * mounted when the browser acts on the keystroke, so focus walks into a list that is about to
   * disappear. Fields whose list holds nothing focusable can leave this out.
   */
  hideList?: () => void;
  /** Extra teardown when the list closes by key — clearing an inline search term, say. */
  onClose?: () => void;
}

export function useDropdownKeyboard<T>(options: UseDropdownKeyboardOptions<T>) {
  /** Index into `items`, or -1 for "no row is current". */
  const highlightedIndex = ref(-1);

  function isHighlighted(index: number): boolean {
    return highlightedIndex.value === index;
  }

  /** Where the highlight belongs when there is no current row to keep. */
  function landingIndex(): number {
    const count = options.items.value.length;
    if (count === 0) return -1;
    // A typed filter overrides the "open on the current value" preference: once the user has typed, the
    // row they mean is the best match, not the value they are in the middle of replacing.
    if (options.searchTerm?.value) return 0;
    const preferred = options.initialIndex?.() ?? 0;
    return preferred >= 0 && preferred < count ? preferred : 0;
  }

  /**
   * Keeps the highlighted row inside the scrolling list. `center` places it mid-list, which is for the
   * initial landing; otherwise it scrolls the least amount needed, which is what arrow keys want.
   */
  function scrollHighlightIntoView(center = false) {
    void nextTick(() => {
      const list = options.listEl.value;
      const index = highlightedIndex.value;
      if (!list || index < 0) return;

      const row = list.children[index] as HTMLElement | undefined;
      if (!row) return;

      if (center) {
        list.scrollTop = row.offsetTop - list.clientHeight / 2 + row.offsetHeight / 2;
        return;
      }

      const viewTop = list.scrollTop;
      const viewBottom = viewTop + list.clientHeight;
      const rowTop = row.offsetTop;
      const rowBottom = rowTop + row.offsetHeight;

      if (rowTop < viewTop) list.scrollTop = rowTop;
      else if (rowBottom > viewBottom) list.scrollTop = rowBottom - list.clientHeight;
    });
  }

  function move(step: 1 | -1) {
    const count = options.items.value.length;
    if (count === 0) return;
    // Wraps at both ends so one held-down arrow cycles the whole list. Not a trap: Tab and Escape
    // still leave, and the list is what wraps, never focus.
    // From "nothing current" either direction lands on the first row rather than off the end.
    const next = highlightedIndex.value < 0 ? 0 : (highlightedIndex.value + step + count) % count;
    highlightedIndex.value = next;
    scrollHighlightIntoView();
  }

  /** True while a pointer press on this field is in flight — see {@link handlePointerDown}. */
  let pointerPressed = false;
  /** True only for the instant we move focus ourselves — see {@link restoreFocus}. */
  let restoringFocus = false;
  /** True while the list is being taken down — see the `isOpen` watcher. */
  let closing = false;

  /**
   * Bind to `pointerdown` on the field.
   *
   * A press focuses the field, and opening the list from that `focus` event puts the popover on screen
   * *between* pointerdown and pointerup. The platform's light-dismiss records what was pressed and acts
   * on release: the release lands on the trigger, which is outside the popover, so it reads as "clicked
   * away" and shuts the list again — it flashes open and closed under the cursor. Deferring to the click
   * handler, which runs after the press has completed, is what keeps a mouse click simply opening it.
   */
  function handlePointerDown() {
    pointerPressed = true;
  }

  /**
   * Bind to `focus` (or `focusin`) on the field.
   *
   * Opens the list, except for a focus no user asked for: one this composable caused itself, one a
   * pointer press is about to complete, or one the browser handed back as the list came down.
   */
  function handleFocus() {
    if (restoringFocus || pointerPressed || closing) return;
    options.open();
  }

  // Cleared wherever the press ends, not just on a completed click: a press that drags off the field
  // never produces a click here, and a flag left standing would swallow the next keyboard focus.
  useEventListener(document, ["pointerup", "pointercancel"], () => {
    pointerPressed = false;
  });

  /**
   * Puts focus back on the field when it has nowhere else to be.
   *
   * The bracket around `.focus()` is load-bearing. Focus lands on the very element whose `focusin`
   * opens the list, so without it every restore re-opens what had just been closed: Escape would close
   * and immediately re-open, and a commit would leave the list standing. `focus()` dispatches its
   * events synchronously, so the flag covers exactly them and nothing else.
   */
  function restoreFocus() {
    const anchor = options.focusAnchor?.();
    if (!anchor || document.activeElement === anchor) return;

    restoringFocus = true;
    try {
      anchor.focus();
    } finally {
      restoringFocus = false;
    }
  }

  /**
   * Whether whatever holds focus can still hold it.
   *
   * Deliberately not a `activeElement === document.body` test: browsers reset that during style recalc,
   * and whether they have got round to it by the time this runs is not something to rely on. The two
   * questions asked instead are the two ways focus dies here — the element was unmounted with the
   * popover that held it, or it was given `display: none` when a single-select tag input hid its
   * inline input — and both force the computation that makes the answer current.
   *
   * `offsetParent` alone would be shorter and wrong: it is also null for a `position: fixed` element,
   * which is very much alive and is what a popover's own contents are.
   */
  function focusIsAlive(): boolean {
    const active = document.activeElement;
    if (!(active instanceof HTMLElement)) return false;
    if (active === document.body) return false;
    if (!active.isConnected) return false;
    // Default options on purpose: a popover wears `visibility: hidden` while it positions, and focus
    // inside one is alive.
    if (typeof active.checkVisibility === "function") return active.checkVisibility();
    return active.offsetParent !== null;
  }

  /**
   * Commits a row and makes sure focus survives it.
   *
   * Internal, and reached only from `Enter`. Routing the components' own mouse handlers through it too
   * was tried and reverted on 2026-08-18: it is the mouse paths that make the surrounding behaviour
   * (`@click` on the field, `pointerup` timing, a popover unmounting mid-click) unpredictable, and the
   * gain did not justify it. A mouse pick keeps whatever focus the component itself leaves behind.
   *
   * Committing can take the focused element out of the layout — a single-select tag input hides its
   * inline input once it is full — and focus then falls to `<body>`, from where Tab restarts at the top
   * of the document. The check runs after the DOM has settled, because that is when the element goes.
   */
  function commitItem(item: T) {
    options.commit(item);
    void nextTick(() => {
      if (focusIsAlive()) return;
      restoreFocus();
    });
  }

  function closeByKey() {
    // Order matters: the synchronous hide is guarded by the open state, and it has to happen before
    // the browser gets the keystroke back.
    options.hideList?.();
    options.isOpen.value = false;
    options.onClose?.();
    restoreFocus();
  }

  function handleKeyDown(event: KeyboardEvent): void {
    if (options.isInert()) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (!options.isOpen.value) {
          options.open();
          return;
        }
        move(1);
        break;

      case "ArrowUp":
        if (!options.isOpen.value) return;
        event.preventDefault();
        move(-1);
        break;

      case "Enter": {
        if (!options.isOpen.value) {
          event.preventDefault();
          options.open();
          return;
        }
        const item = options.items.value[highlightedIndex.value];
        // An empty list, or one still loading, has nothing to commit — leave the keystroke to whatever
        // else on the page wants it rather than swallowing it silently.
        if (item === undefined) return;
        event.preventDefault();
        commitItem(item);
        break;
      }

      case "Escape":
        if (!options.isOpen.value) return;
        event.preventDefault();
        closeByKey();
        break;

      // Deliberately *not* prevented: the press closes the list and then travels on, so a single Tab
      // both dismisses the options and moves to the next field. That is only safe because `hideList`
      // has already taken the popover out of the tab order.
      case "Tab":
        if (!options.isOpen.value) return;
        closeByKey();
        break;
    }
  }

  watch(options.isOpen, (open) => {
    if (!open) {
      highlightedIndex.value = -1;

      // Taking the list down moves focus without anyone asking: for a field whose popover renders
      // inside it, the focused row, search input or apply button disappears and the browser re-homes
      // focus onto the field itself — whose `focusin` is what opens the list. Unguarded, closing by
      // apply or by clicking away re-opens instantly, and the field is left reading "open" with no
      // popover on screen. `restoringFocus` covers only the focus this composable moves itself.
      //
      // Pre-flush, so the flag is up before Vue patches the popover out and the browser reacts.
      closing = true;
      void nextTick(() => {
        closing = false;
      });
      return;
    }
    highlightedIndex.value = landingIndex();
    scrollHighlightIntoView(true);
  });

  if (options.searchTerm) {
    watch(options.searchTerm, () => {
      if (!options.isOpen.value) return;
      highlightedIndex.value = landingIndex();
    });
  }

  // The list also changes without the term changing, in two ways that both land here.
  watch(
    () => options.items.value.length,
    (length) => {
      if (!options.isOpen.value) return;

      if (length === 0) {
        highlightedIndex.value = -1;
        return;
      }

      // A server-backed list usually arrives *after* the field opened: opening asks the consumer to
      // fetch. So the highlight the open could not place — there was nothing to place it on — is placed
      // here instead, or Enter would stay dead until an arrow key woke it up.
      if (highlightedIndex.value < 0) {
        highlightedIndex.value = landingIndex();
        scrollHighlightIntoView(true);
        return;
      }

      // Committing a row can take it out of the candidates. Clamping rather than resetting is what
      // makes picking several in a row work: the row that slides into the vacated index is the next
      // one, which is exactly where the highlight belongs. It also guards correctness — an index past
      // the end would commit the wrong row, or none.
      if (highlightedIndex.value > length - 1) highlightedIndex.value = length - 1;
    },
  );

  return {
    highlightedIndex,
    isHighlighted,
    handleKeyDown,
    handleFocus,
    handlePointerDown,
    /**
     * Moves focus to the field's own tab stop without the list treating it as "the user asked for me".
     *
     * For a component that is about to remove the element holding focus — a single-select tag input
     * hiding its inline input the moment the field is full — calling this first is what keeps the caret
     * (and therefore the field's focused underline) on the field instead of dropping it to `<body>`.
     */
    focusField: restoreFocus,
  };
}

interface DropdownHandle {
  hideNow: () => void;
}

function isDropdownHandle(el: unknown): el is DropdownHandle {
  return !!el && typeof (el as DropdownHandle).hideNow === "function";
}

/**
 * Holds the `SCADropdown` instance for {@link UseDropdownKeyboardOptions.hideList}, bound with `:ref`.
 *
 * `ref="name"` would do the same job, but it makes vue-tsc inline the dropdown's entire public instance
 * type — `$nextTick` included — into the consumer's emitted `.d.ts`. TypeScript prints that member as a
 * bare `nextTick` rather than `typeof nextTick` in any file that does not itself import `nextTick`, and
 * api-extractor then dies rolling the library types up: `Unable to follow symbol for "nextTick"`. A
 * callback ref keeps the type out of the file entirely.
 */
export function useDropdownHandle() {
  // Shallow: this holds a component's public proxy, which has no business being made deeply
  // reactive. `useTemplateRef` does the same.
  const dropdown = shallowRef<DropdownHandle | null>(null);

  return {
    setDropdown(el: unknown) {
      // Checked rather than cast: a silent no-op here brings back the bug `hideNow` exists to fix —
      // Tab walking into a popover that is about to unmount — and no type would catch it, because
      // the callback ref is what keeps the dropdown's type out of this file in the first place.
      dropdown.value = isDropdownHandle(el) ? el : null;
    },
    hideList() {
      dropdown.value?.hideNow();
    },
  };
}
